import { useEffect, useMemo, useState } from 'react';
import { CustomEventsTable } from './CustomEventsTable';
import { CustomEventsTimeline } from './CustomEventsTimeline';
import { Checkbox } from '@nimbus-ds/components';
import { experimentService } from '@/api/experiments';
import { funnelService } from '@/api/funnel';
import { listCustomEvents } from '@/api/customEvents';
import type { CustomEventMetrics, CustomEventsPropensity } from '@/api/customEvents';

interface CustomEventsSectionProps {
  storeId?: string;
  experimentId?: string;
  windowDays?: number;
  /** Pre-fetched funnel data - pass null while loading, undefined to let component fetch itself */
  funnelData?: any | null;
}

/**
 * Custom Events Section Component
 * Displays custom event metrics for store or experiment analytics
 *
 * Usage:
 *
 * // Store-level analytics
 * <CustomEventsSection storeId={storeId} windowDays={7} />
 *
 * // Experiment-level analytics
 * <CustomEventsSection storeId={storeId} experimentId="exp-001" windowDays={30} />
 */
export function CustomEventsSection({
  storeId,
  experimentId,
  windowDays = 7,
  funnelData,
}: CustomEventsSectionProps) {
  const [customEvents, setCustomEvents] = useState<CustomEventMetrics[]>([]);
  const [summary, setSummary] = useState<{
    total_events: number;
    unique_visitors_with_events: number;
    total_event_definitions: number;
  } | null>(null);
  const [propensity, setPropensity] = useState<CustomEventsPropensity | null>(null);
  // Loading if: parent is providing data (funnelData !== undefined) but hasn't loaded yet
  // OR: component will fetch itself (funnelData === undefined)
  const [isLoading, setIsLoading] = useState(funnelData === undefined || !funnelData);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [archivedIds, setArchivedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    listCustomEvents(storeId)
      .then((res) => {
        if (cancelled) return;
        setArchivedIds(new Set(
          (res.custom_events || [])
            .filter((e) => e.status === 'archived')
            .map((e) => e.event_id)
        ));
      })
      .catch((err) => console.error('Failed to load event definitions:', err));
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const visibleEvents = useMemo(() => {
    if (includeArchived || archivedIds.size === 0) return customEvents;
    return customEvents.filter((e) => !archivedIds.has(e.event_id));
  }, [customEvents, archivedIds, includeArchived]);

  const hideForTimeline = useMemo(
    () => (includeArchived ? new Set<string>() : archivedIds),
    [archivedIds, includeArchived]
  );

  // Extract custom events from provided funnelData
  useEffect(() => {
    if (funnelData) {
      const tnxAnalytics = funnelData.tnx_analytics || funnelData;

      // Debug: verify propensity data is present
      if (process.env.NODE_ENV === 'development') {
        console.log('[CustomEvents] Propensity data:', tnxAnalytics.custom_events_propensity);
      }

      if (tnxAnalytics?.custom_events && tnxAnalytics.custom_events.length > 0) {
        setCustomEvents(tnxAnalytics.custom_events);
        setSummary(tnxAnalytics.custom_events_summary || null);
        setPropensity(tnxAnalytics.custom_events_propensity || null);
      } else {
        setCustomEvents([]);
        setSummary(null);
        setPropensity(null);
      }
      setIsLoading(false);
    }
  }, [funnelData]);

  // Only fetch if funnelData prop is undefined (meaning: "fetch yourself")
  // If funnelData is null, parent is handling fetch and we should wait
  useEffect(() => {
    if (funnelData !== undefined) return; // Parent is handling data, don't fetch

    async function fetchCustomEvents() {
      if (!storeId) return;

      setIsLoading(true);
      setError(null);

      try {
        if (experimentId) {
          // Fetch experiment-level custom events
          const response = await experimentService.getExperimentFunnel(
            experimentId,
            windowDays
          );

          // Extract custom events from TNX analytics
          const tnxAnalytics = response.tnx_analytics;
          if (tnxAnalytics?.custom_events) {
            setCustomEvents(tnxAnalytics.custom_events);
            setSummary(tnxAnalytics.custom_events_summary || null);
            setPropensity(tnxAnalytics.custom_events_propensity || null);
          } else {
            setCustomEvents([]);
            setSummary(null);
            setPropensity(null);
          }
        } else {
          // Fetch store-level custom events from store funnel analytics
          const response = await funnelService.getStoreFunnelAnalytics(storeId, windowDays);

          // Extract custom events from TNX analytics or top level
          const tnxAnalytics = response.tnx_analytics || response;
          if (tnxAnalytics?.custom_events && tnxAnalytics.custom_events.length > 0) {
            setCustomEvents(tnxAnalytics.custom_events);
            setSummary(tnxAnalytics.custom_events_summary || null);
            setPropensity(tnxAnalytics.custom_events_propensity || null);
          } else {
            setCustomEvents([]);
            setSummary(null);
            setPropensity(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch custom events:', err);
        setError('Failed to load custom event metrics');
        setCustomEvents([]);
        setSummary(null);
        setPropensity(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCustomEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, experimentId, windowDays]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  const showTimeline = !experimentId && !!storeId;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Checkbox
          name="show-archived-events"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
          label="Incluir eventos archivados"
        />
      </div>
      {showTimeline ? (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <CustomEventsTable
            events={visibleEvents}
            summary={summary || undefined}
            propensity={propensity || undefined}
            isLoading={isLoading}
          />
          <CustomEventsTimeline
            storeId={storeId!}
            windowDays={windowDays}
            hideEventIds={hideForTimeline}
          />
        </div>
      ) : (
        <CustomEventsTable
          events={visibleEvents}
          summary={summary || undefined}
          propensity={propensity || undefined}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

export default CustomEventsSection;
